<template>
  <div class="page-container">
    <div class="title-header">
      <div class="title">
        Sepette İndirim Kampanya Teklifleri: {{ campaignName }}
      </div>
    </div>

    <!-- Filter Container -->
    <div class="content-wrapper">
      <ManageCampaignFilters
        ref="filtersRef"
        :filters="filters"
        :categories="categories"
        :brands="brands"
        @update:filters="handleFiltersUpdate"
        @apply-filters="applyFilters"
        @clear-filters="clearFilters"
      />
    </div>

    <ManageCampaignTable
      :isLoading="isLoading"
      :products="products"
      :current-page="currentPage"
      :total-pages="totalPages"
      :page-size="pageSize"
      :sort-by="sortBy"
      :show-excel-button="false"
      :show-another-campaign-badge="true"
      :is-first-join="true"
      @page-change="handlePageChange"
      @page-size-change="handlePageSizeChange"
      @sort-change="handleSortChange"
      @selection-change="handleProductSelectionChange"
      @price-change="handleProductPriceChange"
    />

    <!-- Sticky Footer -->
    <div class="sticky-footer">
      <div class="sticky-footer-content">
        <span v-if="selectedProducts.length > 0" class="selected-count">
          {{ selectedProducts.length }} Ürün Seçildi
        </span>
        <AppButton
          primary
          md
          title="Kampanyaya Katıl"
          :disabled="selectedProducts.length === 0"
          @buttonClick="handleAddToCampaign"
        />
      </div>
    </div>

    <!-- Failed Products Modal -->
    <FailedProductsModal ref="failedProductsModalRef" @closed="onFailedProductsModalClosed" />

    <!-- Add to Campaign Modal -->
    <Modal
      ref="addToCampaignModalRef"
      :show-icon="false"
      :modalStyle="{ width: '500px' }"
      positive-action-text="Onayla"
      negative-action-text="Geri Dön"
      @on-positive-action-click="handleConfirmAddToCampaign"
      @on-negative-action-click="handleCloseAddToCampaignModal"
      @modal-closed="onAddToCampaignModalClosed"
    >
      <template #body>
        <div class="modal-body-content">
          <Icons name="InfoBlueCircle" color="#4FA4E5" size="lg" />
          <p>Kampanyada yer alması için {{ selectedProducts.length }} ürün seçtiniz. Kampanyaya katılmayı onaylıyor musunuz?</p>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { getCampaignDetail, getSellerInfo, getCampaignProducts, joinCampaign } from './services/umbrellaCampaignService';
import { AppButton, Modal, Icons } from '@n11/so-ui-components';
import { ToastMessageTypes } from '@/utils/enum/toastMessageTypes';
import { sendFilterShowResultEvent, sendJoinCampaignDismissEvent, sendJoinCampaignSuccessEvent, sendJoinCampaignFailEvent } from '@/utils/ga4/helper';
import ManageCampaignFilters from './partials/ManageCampaignFilters.vue';
import ManageCampaignTable from './partials/ManageCampaignTable.vue';
import FailedProductsModal from './partials/FailedProductsModal.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();

const campaignId = ref(route.params.campaignId || null);
const campaignName = ref('');
const campaignData = ref({});
const isLoading = ref(false);

const filters = ref({
  product: '',
  stockAmount: '',
  stockCode: '',
  category: [],
  brand: [],
});
const categories = ref([]);
const brands = ref([]);
const sortBy = ref('');
const currentPage = ref(0);
const totalPages = ref(0);
const pageSize = ref(10);
const products = ref([]);
const selectedProducts = ref([]);
const selectedProductPrices = ref({});

const filtersRef = ref(null);
const addToCampaignModalRef = ref(null);
const failedProductsModalRef = ref(null);
const pendingFailedProducts = ref(null);
const joinSucceeded = ref(false);
const allSelectedProductsMap = ref({});

const fetchCampaignData = async () => {
  if (!campaignId.value) {
    campaignName.value = '';
    return;
  }
  
  isLoading.value = true;
  try {
    const data = await getCampaignDetail(campaignId.value);
    campaignData.value = data;
    campaignName.value = data.name || '';
  } catch (error) {
    console.error('Error fetching campaign data:', error);
    campaignData.value = {};
    campaignName.value = '';
  } finally {
    isLoading.value = false;
  }
};

const fetchProducts = async () => {
  if (!campaignId.value) return;
  
  isLoading.value = true;
  try {
    // Search criteria oluştur - includedInCampaign: false (kampanyaya eklenebilecek ürünler)
    const stockAmountTrimmed = String(filters.value.stockAmount ?? '').trim();
    const stockAmount =
      /^\d+$/.test(stockAmountTrimmed) ? Number(stockAmountTrimmed) : null;

    const searchCriteria = {
      includedInCampaign: false,
      page: currentPage.value,
      size: pageSize.value,
      ...(filters.value.product && filters.value.product.trim() && { 
        productName: filters.value.product.trim() 
      }),
      ...(stockAmount !== null && { stock: stockAmount }),
      ...(filters.value.stockCode && filters.value.stockCode.trim() && { 
        sellerStockCode: filters.value.stockCode.trim() 
      }),
      ...(filters.value.category && filters.value.category.length > 0 && { 
        categoryIds: filters.value.category.map((c) => c.id)
      }),
      ...(filters.value.brand && filters.value.brand.length > 0 && { 
        brandNames: filters.value.brand.map((b) => b.name)
      }),
    };
    
    const result = await getCampaignProducts(campaignId.value, searchCriteria);
    
    products.value = result.products || [];
    products.value = products.value.map((product) => {
      const editedPrice = selectedProductPrices.value[product.id];
      if (!editedPrice) {
        return product;
      }

      return {
        ...product,
        price: editedPrice.minPrice,
      };
    });
    totalPages.value = result.totalPages || 0;
  } catch (error) {
    console.error('Error fetching products:', error);
    products.value = [];
    totalPages.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const handleFiltersUpdate = (newFilters) => {
  filters.value = { ...newFilters };
};

const buildFilterAnalyticsPayload = () => {
  const groups = [];

  if (filters.value.product && filters.value.product.trim()) {
    groups.push({
      name: 'Ürün Adı',
      values: [filters.value.product.trim()],
    });
  }

  if (filters.value.stockAmount && String(filters.value.stockAmount).trim()) {
    groups.push({
      name: 'Stok Adet',
      values: [String(filters.value.stockAmount).trim()],
    });
  }

  if (filters.value.stockCode && filters.value.stockCode.trim()) {
    groups.push({
      name: 'Stok Kodu',
      values: [filters.value.stockCode.trim()],
    });
  }

  if (filters.value.category && filters.value.category.length > 0) {
    const categoryNames = filters.value.category
      .map((item) => item.name || item.label || '')
      .filter(Boolean);
    if (categoryNames.length > 0) {
      groups.push({
        name: 'Kategori',
        values: categoryNames,
      });
    }
  }

  if (filters.value.brand && filters.value.brand.length > 0) {
    const brandNames = filters.value.brand
      .map((item) => item.name || item.label || '')
      .filter(Boolean);
    if (brandNames.length > 0) {
      groups.push({
        name: 'Marka',
        values: brandNames,
      });
    }
  }

  const filter_count = groups.length;

  const selected_filters = groups
    .map((g) => `${g.name}:${g.values.join('|')}`)
    .join(', ');

  const filtersDetail = groups.map((g) => ({
    filter_name: g.name,
    selected_values: g.values,
    selected_count: g.values.length,
  }));

  return {
    filter_count,
    selected_filters,
    filters: filtersDetail,
  };
};

const applyFilters = () => {
  currentPage.value = 0;
  selectedProducts.value = []; // Filtre uygulandığında seçimleri temizle
  allSelectedProductsMap.value = {};

   const { filter_count, selected_filters, filters: filtersDetail } = buildFilterAnalyticsPayload();
   if (filter_count > 0) {
     sendFilterShowResultEvent({
       page_type: 'Campaign Detail',
       filter_count,
       selected_filters,
       filters: filtersDetail,
     });
   }

  fetchProducts();
};

const clearFilters = () => {
  filters.value = {
    product: '',
    stockAmount: '',
    stockCode: '',
    category: [],
    brand: [],
  };
  currentPage.value = 0;
  selectedProducts.value = []; // Filtre temizlendiğinde seçimleri temizle
  allSelectedProductsMap.value = {};
  fetchProducts();
};

const handlePageChange = (pageIndex) => {
  currentPage.value = pageIndex;
  fetchProducts();
};

const handlePageSizeChange = (newPageSize) => {
  pageSize.value = Number(newPageSize);
  currentPage.value = 0;
  fetchProducts();
};

const handleSortChange = (selectedSort) => {
  sortBy.value = selectedSort;
  currentPage.value = 0;
  fetchProducts();
};

const handleProductSelectionChange = (selectedIds) => {
  selectedProducts.value = selectedIds;

  // Add current page products that are selected to the map
  products.value.forEach(product => {
    if (selectedIds.includes(product.id)) {
      allSelectedProductsMap.value[String(product.id)] = product;
    }
  });

  // Remove deselected products from the map
  Object.keys(allSelectedProductsMap.value).forEach(key => {
    if (!selectedIds.some(id => String(id) === key)) {
      delete allSelectedProductsMap.value[key];
    }
  });
};

const handleProductPriceChange = (priceData) => {
  selectedProductPrices.value[priceData.id] = priceData;
};

const toNumericPrice = (value) => {
  if (typeof value === 'number') return value;
  if (value === null || value === undefined || value === '') return 0;

  const normalizedValue = String(value)
    .replace(/\./g, '')
    .replace(',', '.');
  const parsedValue = Number(normalizedValue);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const handleAddToCampaign = () => {
  if (addToCampaignModalRef.value) {
    addToCampaignModalRef.value.openModal();
  }
};

const handleCloseAddToCampaignModal = () => {
  if (addToCampaignModalRef.value) {
    addToCampaignModalRef.value.hiddenModal();
  }
};

const persistNewlyJoinedCampaignForList = () => {
  const formattedCommissionDiscount =
    campaignData.value.commissionDiscount &&
    Number(campaignData.value.commissionDiscount) !== 0
      ? `%${campaignData.value.commissionDiscount} Komisyon İndirimi`
      : '';

  const newlyJoinedCampaign = {
    id: campaignId.value,
    name: campaignName.value,
    commissionDiscount: formattedCommissionDiscount,
    status: 'Devam Ediyor',
    statusClass: 'status-ongoing',
    dateRange: campaignData.value.dateRange || '',
    endDate: campaignData.value.dateRange
      ? campaignData.value.dateRange.split(' - ')[1]
      : '',
    isNewlyJoined: true,
  };

  sessionStorage.setItem(
    'newlyJoinedCampaign',
    JSON.stringify(newlyJoinedCampaign),
  );
};

const onFailedProductsModalClosed = () => {
  window.location.href = '/campaigns/umbrella?tab=joined';
};

const onAddToCampaignModalClosed = () => {
  if (pendingFailedProducts.value && pendingFailedProducts.value.length > 0) {
    const products = pendingFailedProducts.value;
    pendingFailedProducts.value = null;
    failedProductsModalRef.value?.openModal(products);
  } else if (!joinSucceeded.value) {
    const selectedProductsData = selectedProducts.value
      .map(id => allSelectedProductsMap.value[String(id)])
      .filter(Boolean);
    sendJoinCampaignDismissEvent({
      button_name: 'Filtrele',
      button_type: 'Filter',
      campaign_id: campaignId.value,
      campaign_name: campaignName.value,
      item_id: selectedProductsData.map(p => p.id).join('|'),
      value: selectedProductsData.map(p => toNumericPrice(selectedProductPrices.value[p.id]?.minPrice ?? p.price)).join('|'),
      price: selectedProductsData.map(p => p.price || '').join('|'),
      commission_rate: selectedProductsData.map(p => p.commissionRate || '').join('|'),
    });
  }
  joinSucceeded.value = false;
};

const handleConfirmAddToCampaign = async () => {
  if (!campaignId.value || selectedProducts.value.length === 0) {
    return;
  }
  
  isLoading.value = true;
  
  try {
    const selectedProductsData = selectedProducts.value
      .map(id => allSelectedProductsMap.value[String(id)])
      .filter(Boolean);

    const productPrices = selectedProductsData.map(product => ({
      productId: product.productId || product.id,
      minPrice: toNumericPrice(selectedProductPrices.value[product.id]?.minPrice ?? product.price),
      maxPrice: selectedProductPrices.value[product.id]?.maxPrice ?? (product.maxPrice || 0),
    }));

    const result = await joinCampaign(campaignId.value, productPrices);

    const failedProducts = result.failedProducts || [];
    const hasFailedProducts = failedProducts.length > 0;
    const failedProductIds = new Set(failedProducts.map(p => p.id || p.productId));
    const successfulProductsData = selectedProductsData.filter(
      p => !failedProductIds.has(p.id) && !failedProductIds.has(p.productId)
    );

    if (hasFailedProducts) {
      sendJoinCampaignFailEvent({
        button_name: 'Filtrele',
        button_type: 'Filter',
        campaign_id: campaignId.value,
        campaign_name: campaignName.value,
        item_id: failedProducts.map(p => p.id || p.productId).join('|'),
        value: failedProducts.map(p => toNumericPrice(selectedProductPrices.value[p.id]?.minPrice ?? p.price)).join('|'),
        price: failedProducts.map(p => p.price || '').join('|'),
        commission_rate: failedProducts.map(p => p.commissionRate || '').join('|'),
      });

      if (result.success && successfulProductsData.length > 0) {
        sendJoinCampaignSuccessEvent({
          button_name: 'Filtrele',
          button_type: 'Filter',
          campaign_id: campaignId.value,
          campaign_name: campaignName.value,
          item_id: successfulProductsData.map(p => p.id).join('|'),
          value: successfulProductsData.map(p => toNumericPrice(selectedProductPrices.value[p.id]?.minPrice ?? p.price)).join('|'),
          price: successfulProductsData.map(p => p.price || '').join('|'),
          commission_rate: successfulProductsData.map(p => p.commissionRate || '').join('|'),
        });
      }

      if (result.success) {
        persistNewlyJoinedCampaignForList();
      }

      pendingFailedProducts.value = failedProducts;
      handleCloseAddToCampaignModal();
    } else if (result.success) {
      sendJoinCampaignSuccessEvent({
        button_name: 'Filtrele',
        button_type: 'Filter',
        campaign_id: campaignId.value,
        campaign_name: campaignName.value,
        item_id: selectedProductsData.map(p => p.id).join('|'),
        value: selectedProductsData.map(p => toNumericPrice(selectedProductPrices.value[p.id]?.minPrice ?? p.price)).join('|'),
        price: selectedProductsData.map(p => p.price || '').join('|'),
        commission_rate: selectedProductsData.map(p => p.commissionRate || '').join('|'),
      });

      joinSucceeded.value = true;
      handleCloseAddToCampaignModal();
      persistNewlyJoinedCampaignForList();

      window.location.href = '/campaigns/umbrella?tab=joined';
    } else {
      handleCloseAddToCampaignModal();
    }
  } catch (error) {
    console.error('Error joining campaign:', error);
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => route.params.campaignId,
  async (newCampaignId) => {
    if (!newCampaignId) return;
    const normalizedNew = String(newCampaignId);
    const normalizedCurrent = campaignId.value ? String(campaignId.value) : null;
    if (normalizedCurrent === normalizedNew) return;

    campaignId.value = newCampaignId;
    await fetchCampaignData();
  },
);

onMounted(async () => {
  if (route.params.campaignId) {
    campaignId.value = route.params.campaignId;
  }
  
  // Seller info API'den categories ve brands'i al
  try {
    const sellerInfo = await getSellerInfo(campaignId.value);
    categories.value = sellerInfo.categories || [];
    brands.value = sellerInfo.brands || [];
  } catch (error) {
    console.error('Error fetching seller info:', error);
  }
  
  // Kampanya detayını ve ürünleri yükle
  await fetchCampaignData();
  await fetchProducts();
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';

.page-container {
  display: flex;
  width: 100%;
  flex-direction: column;

  .title-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;

    .title {
      color: $colorN80;
      font-size: 20px;
      font-weight: 700;
      letter-spacing: -0.2px;
      line-height: 26px;
    }
  }

  .content-wrapper {
    background: $colorN00;
    border-radius: 8px;
    border-bottom: 0.5px solid $colorN30;
    display: flex;
    flex-direction: column;
  }
  :deep(.popover-wrapper__content) {
    max-width: 250px !important;
    width: auto !important;
    transform: translateX(-15%) !important;
    position: relative;
    z-index: 9999 !important;
  }

  :deep(.popover-wrapper),
  :deep(.popover),
  :deep(.popover-content),
  :deep(.popover__content) {
    z-index: 9999 !important;
  }
}

.sticky-footer {
  position: fixed;
  z-index: 10;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  padding: 20px 200px;
  border-top: 0.5px solid $colorN30;
  background: $colorN00;

  .sticky-footer-content {
    display: flex;
    align-items: center;
    gap: 16px;

    .selected-count {
      color: $colorN80;
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      letter-spacing: -0.05px;
    }
  }

  :deep(button) {
    width: 193px;
  }
}

.modal-body-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  :deep(.icon) {
    width: 60px;
    height: 60px;

    svg {
      width: 60px !important;
      height: 60px !important;
    }
  }

  p {
    margin: 0;
    color: $colorN80;
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: -0.02px;
    text-align: center;
  }
}
</style>
